import { defineStore } from 'pinia';
import { login as loginService, recuperarClave, nuevaClave, miUsuario, loginGoogle, resetPageLoadCache } from '@/services/usuarios';
import router from '@/router';
import { Buffer } from 'safe-buffer';
import strapiService from '@/services/strapi';

// Funcion que decide si debe utilizar el router para redireccionar o es una url externa
const redireccionar = (url) => {
    if (url.startsWith('http')) {
        window.location.href = url;
    } else {
        router.push({ path: url });
    }
}

const setCookie = (cname, cvalue, exdays, domain) => {
    document.cookie = cname + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";

    let d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    let expires = "expires=" + d.toUTCString();
    let cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    if (domain) cookie += ";domain=" + domain;
    document.cookie = cookie;
};

export const useAuthStore = defineStore('auth', {
    state: () => ({
        usuario: null,
        token: null,
        error: null,
        redireccionLogin: null,
        datosAdicionales: null
    }),

    getters: {
        usuarioActual: (state) => state.usuario
    },

    actions: {
        setAfterLogin(url) {
            this.redireccionLogin = url;
        },
        establecerToken(nuevoToken) {
            this.token = nuevoToken;
            localStorage.setItem('token', nuevoToken);
            const tokenCodificado = Buffer.from(`${this.usuario.id}:${nuevoToken}`).toString("base64");
            setCookie('orsai_token', tokenCodificado, 1, 'orsai.org');
        },

        establecerUsuario(datosUsuario) {
            console.log('Estableciendo usuario en el store:', {
                id: datosUsuario?.id,
                email: datosUsuario?.email,
                usuarioCompleto: datosUsuario
            });
            this.usuario = datosUsuario;
        },

        establecerRedireccionLogin(url) {
            this.redireccionLogin = url;
        },

        limpiarSesion() {
            this.usuario = null;
            this.token = null;
            this.error = null;
            this.redireccionLogin = null;
            this.datosAdicionales = null;
        },

        async iniciarSesion(credenciales, redireccion) {
            this.error = null;
            try {
                const respuesta = await loginService(credenciales);
                if (respuesta.login) {
                    this.establecerUsuario(respuesta.usuario);
                    this.establecerToken(respuesta.usuario.token);
                    const datosActualizados = await miUsuario();
                    this.establecerUsuario(datosActualizados);
                    if (redireccion) {
                        router.push(redireccion);
                    } else if (this.redireccionLogin) {
                        router.push(this.redireccionLogin);
                        this.redireccionLogin = null;
                    } else {
                        router.push('/mi-suscripcion');
                    }
                    
                    return true;
                } else {
                    this.limpiarSesion();
                    this.error = "Correo o contraseña incorrectos. Por favor, intentá nuevamente.";
                    return false;
                }
            } catch (e) {
                this.error = "Error al iniciar sesión. Por favor, intentá nuevamente.";
                return false;
            }
        },

        cerrarSesion() {
            this.limpiarSesion();
            localStorage.removeItem('token');
            resetPageLoadCache();
            setCookie('orsai_token', '', -1, 'orsai.org');
            router.push('/ingresar');
        },

        async inicializarAuth() {
            const tokenGuardado = localStorage.getItem('token');
            if (tokenGuardado) {
                try {
                    const datosUsuario = await miUsuario();
                    if (datosUsuario) {
                        this.establecerUsuario(datosUsuario);
                        // Recodificar el token en base64 como se hace en login
                        const authToken = `${datosUsuario.id}:${tokenGuardado}`;
                        const encodedToken = Buffer.from(authToken).toString('base64');
                        this.token = encodedToken;
                        console.log('✅ Token recodificado en inicializarAuth:', {
                            original: tokenGuardado,
                            encoded: encodedToken ? 'EXISTS' : 'MISSING'
                        });
                    } else {
                        this.cerrarSesion();
                    }
                } catch (e) {
                    console.error('❌ Error en inicializarAuth:', e);
                    this.cerrarSesion();
                }
            }
        },

        async verificarAutenticacion() {
            try {
                const usuario = await miUsuario();
                if (usuario) {
                    this.establecerUsuario(usuario);
                    return true;
                } else {
                    this.cerrarSesion();
                    return false;
                }
            } catch (e) {
                this.cerrarSesion();
                return false;
            }
        },

        async ingresarConGoogle(data, redirect = "/") {
            try {
                this.usuario = await loginGoogle(data);
                const authToken = `${this.usuario.id}:${this.usuario.token}`;
                const encodedToken = Buffer.from(authToken).toString("base64");
                this.token = encodedToken;
                this.isAuthenticated = true;
                this.usuario = await miUsuario();
                this.error = null;

                setCookie('orsai_token', this.token, 1, 'orsai.org');
                // Redireccionar a la página de inicio
                if (redirect) {
                    // Si tiene HTTP, es una URL externa
                    if (redirect.startsWith('http')) {
                        window.location.href = redirect;
                    } else if (redirect === 'no-redirect') {
                        this.loading = false;
                        return true;
                    } else {
                        router.push({ path: redirect });
                    }
                }
            } catch (e) {
                console.error(e);
                this.error = "No se pudo iniciar sesión con Google. Por favor, intentá nuevamente.";
            }
        },
        async cambiarContrasena(clave1, clave2, token) {
            try {
                this.loading = true;
                this.usuario = await nuevaClave({ clave1, clave2, token });
                // Loguearte con la nueva clave
                const authToken = `${this.usuario.id}:${this.usuario.token}`;
                const encodedToken = Buffer.from(authToken).toString("base64");
                this.token = encodedToken;
                this.isAuthenticated = true;

                this.usuario = await miUsuario();


                this.error = null;
                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.error = "No se pudo cambiar la contraseña. Por favor, intentá nuevamente.";
            }
        },
        async enviarLinkRecuperacion(correo) {
            try {
                this.loading = true;
                await recuperarClave(correo);
                this.error = null;
                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.error = "No se reconoce el correo ingresado. Por favor, intentá nuevamente.";
            }
        },
        async login(usuario, redirect = "/") {
            this.loading = true;
            this.error = null;

            console.log('Login', usuario);
            const response = await loginService(usuario);
            if (response.login) {
                this.usuario = response.usuario;
                this.isAuthenticated = true;

                // Calcular token
                let authToken = `${this.usuario.id}:${this.usuario.token}`;
                authToken = Buffer.from(authToken).toString('base64');
                this.token = authToken;

                setCookie('orsai_token', this.token, 1, 'orsai.org');
                // Redireccionar a la página de inicio
                console.log('Redirect', redirect);
                if(redirect) {
                    // Si es la misma ruta, forzar recarga
                    if (router.currentRoute.path === redirect) {
                        console.log('Forzando recarga');
                        router.go();
                    } else if (redirect.startsWith('http')) {
                        window.location.href = redirect;
                    }
                    else {
                        router.push({ path: redirect });
                    }
                } 
                this.loading = false;
                return true;
            } else {
                this.isAuthenticated = false;
                this.error = "Correo o contraseña incorrectos. Por favor, intentá nuevamente.";
                this.loading = false;
                return false;
            }
        },
        logout() {
            // Lógica de cierre de sesión
            this.usuario = null;
            this.isAuthenticated = false;
            this.token = null;
            // Eliminamos el cache de la sesión
            resetPageLoadCache();


            setCookie('orsai_token', '', -1, 'orsai.org');

            router.push({ path: '/ingresar' });
        },
        async estaAutenticado() {
            // Verificar si el usuario está autenticado miUsuario
            try {
                const usuario = await miUsuario();
                if (usuario) {
                    this.usuario = usuario;
                    this.isAuthenticated = true;
                } else {
                    this.isAuthenticated = false;
                }
            } catch (e) {
                console.error("NO ESTÁ AUTENTICADO", e);
                this.isAuthenticated = false;
            }
            return this.isAuthenticated;
        },
        async cargarDatosAdicionales() {
            if (this.datosAdicionales) return; // Si ya tenemos los datos, no los volvemos a cargar
            
            try {
                const response = await strapiService.get(`/api/usuarios/${this.usuario.id}?populate=*`);
                this.datosAdicionales = {
                    fecha_nacimiento: response.data.fecha_nacimiento,
                    pais: response.data.pais,
                    // otros datos que necesites
                };
            } catch (error) {
                console.error('Error al cargar datos adicionales:', error);
            }
        },
        actualizarDatosAdicionales(datos) {
            this.datosAdicionales = { ...this.datosAdicionales, ...datos };
        }
    },
    persist: true, // Habilita el almacenamiento persistente
});
